import React, { useState } from "react";
import { Text } from "@chakra-ui/react";
import { downloadUrl } from "utils";
import { trackWhitepaperDownloaded } from "components/event-tracking";
import { type EmbeddedWhitepaper } from "lib/sanity";
import { type SectionProps } from "components/section";
import { type AuraCardProps } from "ui/cards";
import { Link } from "ui/link";
import { BookFormCard, StyledHeading, StyledText } from "ui/cards/book-form-card";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";

type DownloadWhitepaperProps = {
  /**
   * Determines the color scheme of the aura card and the book cover
   * @default "green"
   */
  variant?: AuraCardProps["variant"];
} & EmbeddedWhitepaper &
  Omit<SectionProps, "variant" | "title">;

/**
 * Lead capture form for an embedded whitepaper download.
 * Uses `shortVariant` for the content. Typically a heading and description.
 */
export function DownloadWhitepaper({
  title,
  fileName,
  fileUrl,
  hubSpotFormId,
  hubSpotCompanyField,
  shortVariant,
  variant = "green",
  ...props
}: DownloadWhitepaperProps) {
  // Destructure and exclude embedded whitepaper props that are not needed for the section
  // Don't want them to be passed to the section props / DOM elements
  const { posterImageUrl, slug, longVariant, ...sectionProps } = props;

  const [success, setSuccess] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const displayedHubspotFields = hubSpotCompanyField ? ["firstname", "lastname", "company"] : ["firstname", "lastname"];
  const fileExtension: string = fileUrl.split(".").pop() ?? "";
  const fileDownloadUrl = `${fileUrl}?dl=${fileName}.${fileExtension}`;

  const onFormSubmit = (lead: Record<string, string>) => {
    trackWhitepaperDownloaded(fileName, lead, "Whitepaper");
    downloadUrl(fileDownloadUrl, `${fileName}.pdf`);
    setSuccess(true);
  };

  const textContent = shortVariant && (
    <ReactMarkdown
      components={{
        h2: ({ children }) => <StyledHeading>{children}</StyledHeading>,
        p: ({ children }) => <StyledText>{children}</StyledText>,
        strong: ({ children }) => (
          <Text as="strong" fontWeight="bold">
            {children}
          </Text>
        ),
      }}
    >
      {shortVariant}
    </ReactMarkdown>
  );

  return (
    <BookFormCard
      variant={variant}
      title={title}
      formLabel="Read the whitepaper"
      formModalLabel="Download whitepaper"
      textContent={textContent}
      formId={hubSpotFormId}
      formFields={displayedHubspotFields}
      successContent={
        success && (
          <Text textStyle="body3">
            Thank you! If your download doesn&apos;t start immediately, please{" "}
            <Link variant="inline" href={fileDownloadUrl}>
              click here
            </Link>
            .
          </Text>
        )
      }
      onFormSubmit={onFormSubmit}
    />
  );
}
